import { trackBaseTemplateUIGA } from "@/lib/analytics";
import {
  trackBaseTemplateUIClickPostHog,
  type BaseTemplateAnalyticsName,
  type BaseTemplateClickSurface,
} from "@/lib/posthogAnalytics";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";

/** Province / city / detection from Cloudflare Worker (`__TEMPLATE_CONFIG__`) for every CTA tap. */
export function workerGeoForBaseTemplateTracking(): Record<
  string,
  string | number | boolean | null | undefined
> {
  if (typeof window === "undefined") return {};
  const cfg = getServerTemplateConfig();
  if (!cfg) return {};
  const cityTrim = cfg.city?.trim();
  const hideCta = cfg.hideCta === true;
  const showCommunity = cfg.showCommunity === true;
  return {
    province: cfg.province ?? null,
    ar_province: cfg.province ?? null,
    ...(cityTrim ? { city: cityTrim, ar_city: cityTrim } : {}),
    site_country: cfg.country ?? null,
    ...(cfg.detectionMethod ? { detection_method: cfg.detectionMethod } : {}),
    ...(cfg.ctaResolvedFrom ? { cta_resolved_from: cfg.ctaResolvedFrom } : {}),
    ...(cfg.ctaResolvedMatched
      ? { cta_resolved_matched: cfg.ctaResolvedMatched }
      : {}),
    hide_cta: hideCta,
    show_community: showCommunity,
    show_birthday: cfg.showBirthday === true,
    show_dual_video: cfg.showDualVideo === true,
    cta_visible:
      !hideCta &&
      (showCommunity ||
        cfg.showBirthday === true ||
        cfg.showWishlist === true ||
        cfg.showDualVideo === true),
    cta_type: hideCta
      ? "none"
      : showCommunity
        ? "community"
        : cfg.showDualVideo
          ? "dual_video"
          : cfg.showBirthday
            ? "birthday"
            : cfg.showWishlist
              ? "wishlist"
              : "none",
  };
}

export type { BaseTemplateAnalyticsName, BaseTemplateClickSurface };

/** Dispatched on each mirrored funnel tap so passive summaries can correlate TTI. */
export const ANA_FUNNEL_TOUCH_AT_EVENT = "anaa:funnel-touch-at";

/**
 * Per-mount session id so the live-funnel admin can group BaseTemplate events
 * with the rest of the visitor's journey. Lazy-initialized on first use and
 * stashed on `window` so reloads in the same tab share it.
 */
export function getBaseTemplateSessionId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const W = window as unknown as { __baseTemplateSessionId?: string };
  if (W.__baseTemplateSessionId) return W.__baseTemplateSessionId;
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `bt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  W.__baseTemplateSessionId = id;
  return id;
}

export function getBaseTemplatePosthogDistinctId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const ph = (window as unknown as {
    posthog?: { get_distinct_id?: () => string };
  }).posthog;
  return ph?.get_distinct_id?.();
}

/** Count of funnel-mirrored BaseTemplate clicks this tab session (see `trackBaseTemplateClick`). */
let trackedInteractionSessionKey: string | undefined;
let trackedInteractionCount = 0;

function syncTrackedInteractionSession(): void {
  const sid = getBaseTemplateSessionId();
  if (!sid) return;
  if (trackedInteractionSessionKey !== sid) {
    trackedInteractionSessionKey = sid;
    trackedInteractionCount = 0;
  }
}

export function getBaseTemplateTrackedInteractionCount(): number {
  if (typeof window === "undefined") return 0;
  syncTrackedInteractionSession();
  return trackedInteractionCount;
}

function bumpBaseTemplateTrackedInteraction(): void {
  syncTrackedInteractionSession();
  trackedInteractionCount += 1;
  try {
    if (typeof window === "undefined" || typeof performance === "undefined") return;
    window.dispatchEvent(
      new CustomEvent<number>(ANA_FUNNEL_TOUCH_AT_EVENT, { detail: performance.now() }),
    );
  } catch {
    /* noop */
  }
}

export function trackBaseTemplateClick(params: {
  templateName: BaseTemplateAnalyticsName;
  elementId: string;
  label: string;
  surface: BaseTemplateClickSurface;
  href?: string;
  countryCode?: string;
  extra?: Record<string, string | number | boolean | null | undefined>;
}) {
  const geo = workerGeoForBaseTemplateTracking();
  const mergedExtra = { ...geo, ...params.extra };

  trackBaseTemplateUIClickPostHog({
    templateName: params.templateName,
    elementId: params.elementId,
    label: params.label,
    surface: params.surface,
    href: params.href,
    countryCode: params.countryCode,
    extra: mergedExtra,
  });
  trackBaseTemplateUIGA({
    templateName: params.templateName,
    elementId: params.elementId,
    label: params.label,
    surface: params.surface,
    href: params.href,
    extra: mergedExtra as Record<string, unknown>,
  });

  // Mirror to /api/track-funnel-event → funnel_events DB + Slack #anaafernandez +
  // /admin/live-funnel. Fire-and-forget; never blocks the click navigation.
  if (typeof window !== "undefined") {
    bumpBaseTemplateTrackedInteraction();
    mirrorFunnelEvent(
      {
        event_name: `base_template_${params.elementId}_click`,
        posthog_distinct_id: getBaseTemplatePosthogDistinctId(),
        session_id: getBaseTemplateSessionId(),
        template: params.templateName,
        flow_name: params.surface,
        landing_path: window.location.pathname,
        country: params.countryCode,
        props: {
          ...geo,
          elementId: params.elementId,
          label: params.label,
          href: params.href,
          ...(params.extra ?? {}),
        },
      },
      { capturePosthog: false },
    );
  }
}
